import { SyntheticEvent, useEffect, useMemo, useState } from "react";

import { User } from "@m7-health/shared-utils";
import { debounce } from "lodash";

// eslint-disable-next-line deprecate/import
import { Autocomplete, Box, TextField, Typography } from "@mui/material";

import { IUser, useListUsersQuery } from "@/api";
import { CustomModal } from "@/common/components";
import { useCurrentUser } from "@/common/hooks";
import { httpClient } from "@/common/packages/httpClient";

const emptyUserList: IUser[] = [];

export const ActAsModal = ({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string | null) => void;
}) => {
  const [search, __setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const currentUser = useCurrentUser();
  const actingAdmin = currentUser.actingAdmin;

  const { data: users = emptyUserList, isLoading } = useListUsersQuery(
    { search, roles: [User.ERole.scheduler, User.ERole.staff] },
    { skip: search?.length <= 2 },
  );

  const setSearch = useMemo(
    () =>
      debounce((_event: SyntheticEvent<Element, Event>, value: string) => {
        __setSearch(value);
      }, 500),
    [],
  );

  const displayResetToAdminAction = !search.length && actingAdmin;
  const [primaryBtnText, primaryBtnEnabled] = (() => {
    if (displayResetToAdminAction) {
      return ["Reset to " + actingAdmin.email, true];
    }
    return selectedUser ? ["Act as " + selectedUser.email, true] : ["Search for a user", false];
  })();

  const setActAs = () => {
    const userToSet = displayResetToAdminAction ? null : selectedUser;
    httpClient.setActAs(userToSet?.id || null);
    onSubmit(userToSet?.email || null);

    onClose();
  };

  // Reset the search when the modal is toggled
  useEffect(() => {
    __setSearch("");
  }, [isOpen]);

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      trackingLabel="actAsModal"
      modalHeaderText="Act as ... 🥷"
      primaryBtnText={primaryBtnText}
      primaryDisabled={!primaryBtnEnabled}
      onSubmit={setActAs}
      modalContent={
        <Box>
          <Typography>Search and select a user to act as ...</Typography>
          <Autocomplete
            loading={isLoading}
            options={users}
            getOptionLabel={(option) =>
              option.email + " (" + option.firstName + " " + option.lastName + ")"
            }
            onInputChange={setSearch}
            onChange={(_event, value) => setSelectedUser(value)}
            renderInput={(params) => (
              <TextField variant="standard" {...params} label="Search a user" />
            )}
          />
        </Box>
      }
    />
  );
};
